import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Pagination,
  Grid,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { SubmissionContext } from '../../../Context/SubmissionProvider';
import { fetchLeaderboardScores, deleteLeaderboardScore, developerApproveFlaggedLeaderboardScore } from './leaderboard-graphql';
import { StyledLoadingButton } from '../../../../../../../../common/Styleguide/Common/Button';
import { COLORS } from '../../../../../../../../common/Styleguide/Common/colors';
import { FrontendLeaderboardScore } from '@crazygames/graph-types/game/leaderboard-scores.type';
import { toast } from 'react-toastify';
import ProcessScoreDialog from './ProcessScoreDialog';
import { formatGraphDateAndTime } from '../../../../../../../../common/formatting';
import config from '../../../../../../../../common/config';
import { DEFAULT_LOCALE } from '../../../../../../../../common/i18n';
import { parseLeaderboardOptions } from '@crazygames/game-types/src/game/leaderboard.type';
import Notification from '../../../../../../../../common/Styleguide/Common/Notification';
import Alert from '../../../../../../../../common/Styleguide/Common/Alert';
import LeaderboardOptionsInfo from './LeaderboardOptionsInfo';
import Flag from '../../../../../../../../common/icons/Flag';

const PAGE_SIZE = 100;

const SubmissionTabLeaderboard: React.FC = () => {
  const { game, submission } = useContext(SubmissionContext);

  const [scores, setScores] = useState<FrontendLeaderboardScore[]>([]);
  const [seasonEndDate, setSeasonEndDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);

  // use cached values unless user refreshes
  const [forceRefresh, setForceRefresh] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scoreToDelete, setScoreToDelete] = useState<FrontendLeaderboardScore | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [approveFlaggedScoreDialogOpen, setApprovedFlaggedScoreDialogOpen] = useState(false);
  const [flaggedScoreToApprove, setFlaggedScoreToApprove] = useState<FrontendLeaderboardScore | null>(null);
  const [approvingFlaggedScore, setApprovingFlaggedScore] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // to keep track of deleted scores and gray them out
  const [deletedScores, setDeletedScores] = useState<Set<string>>(new Set());
  // to keep track of approved (but previously flagged) scores and stop flagging them
  const [approvedFlaggedScores, setApprovedFlaggedScores] = useState<Set<string>>(new Set());

  const [showedConfirmModal, setShowedConfirmModal] = useState(false);

  const leaderboardOptions = useMemo(() => {
    return game?.rawLeaderboardOptions ? parseLeaderboardOptions(JSON.parse(game.rawLeaderboardOptions)) : null;
  }, [game]);

  useEffect(() => {
    const loadScores = async () => {
      if (!game || !leaderboardOptions || !submission) {
        setLoading(false);
        setError('Game or submission not found');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const offset = (page - 1) * pageSize;
        const result = await fetchLeaderboardScores(submission.id, {
          gameId: game.id,
          offset,
          limit: pageSize,
        });

        setScores(result.data.me.submission.leaderboardScores.nodes);
        setTotalCount(result.data.me.submission.leaderboardScores.count);
        setSeasonEndDate(result.data.me.submission.leaderboardScores.seasonEndDate);
        setLoading(false);
        if (forceRefresh) {
          // reset forcing to use cache
          setForceRefresh(false);
        }
      } catch (error) {
        console.error('Failed to load leaderboard scores', error);
        setError('Failed to load leaderboard scores.');
        setLoading(false);
      }
    };

    loadScores();
  }, [page, pageSize, game, leaderboardOptions, submission, forceRefresh]);

  if (!leaderboardOptions) {
    return null;
  }

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleDeleteClick = (score: FrontendLeaderboardScore) => {
    if (showedConfirmModal) {
      executeDelete(score);
    } else {
      setDeleteDialogOpen(true);
      setScoreToDelete(score);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setScoreToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!scoreToDelete) {
      return;
    }

    setShowedConfirmModal(true);

    await executeDelete(scoreToDelete);

    setDeleteDialogOpen(false);
    setScoreToDelete(null);
  };

  const executeDelete = async (score: FrontendLeaderboardScore) => {
    if (!game) {
      return;
    }

    setDeleting(true);

    try {
      await deleteLeaderboardScore({
        gameId: game.id,
        userId: score.userId,
        seasonId: score.seasonId,
      });

      // add to deleted scores set and set the row gray
      setDeletedScores(new Set([...deletedScores, score.userId]));
    } catch (error) {
      console.error('Failed to delete score', error);
      toast.error('Failed to delete score.');
    } finally {
      setDeleting(false);
    }
  };

  const handleApproveFlaggedScoreClick = (score: FrontendLeaderboardScore) => {
    if (showedConfirmModal) {
      executeApproveFlaggedScore(score);
    } else {
      setApprovedFlaggedScoreDialogOpen(true);
      setFlaggedScoreToApprove(score);
    }
  };

  const handleApproveFlaggedScoreCancel = () => {
    setApprovedFlaggedScoreDialogOpen(false);
    setFlaggedScoreToApprove(null);
  };

  const handleApproveFlaggedScoreConfirm = async () => {
    if (!flaggedScoreToApprove) {
      return;
    }

    setShowedConfirmModal(true);

    await executeApproveFlaggedScore(flaggedScoreToApprove);

    setApprovedFlaggedScoreDialogOpen(false);
    setFlaggedScoreToApprove(null);
  };

  const executeApproveFlaggedScore = async (score: FrontendLeaderboardScore) => {
    if (!game) {
      return;
    }

    setApprovingFlaggedScore(true);

    try {
      await developerApproveFlaggedLeaderboardScore({
        gameId: game.id,
        userId: score.userId,
        seasonId: score.seasonId,
      });

      // add to ApproveFlaggedScored scores set, which removes the visual flag from the UI without having to refresh
      setApprovedFlaggedScores(new Set([...approvedFlaggedScores, score.userId]));
    } catch (error) {
      console.error('Failed to developerApproveFlaggedLeaderboardScore score', error);
      toast.error('Failed to approve the flagged score.');
    } finally {
      setApprovingFlaggedScore(false);
    }
  };

  const handleRefresh = () => {
    setDeletedScores(new Set());

    // force fetching scores from network
    setForceRefresh(true);
  };

  const formatScore = (score: number): string => {
    return score.toLocaleString();
  };

  const getUserProfileUrl = (username: string): string => {
    const domainDetails = config.domains[DEFAULT_LOCALE];
    return `${domainDetails.base}${domainDetails.pathname}/u/${username}`;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && scores.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      {leaderboardOptions && <LeaderboardOptionsInfo leaderboardOptions={leaderboardOptions} />}

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Notification
          theme="info"
          extraSx={{ flex: 1 }}
          description={
            <>
              This table shows accepted scores from the current season per user. You can manually remove scores that are deemed hacked. For
              changing your Leaderboard parameters, contact us.
            </>
          }
        />
        <StyledLoadingButton
          sx={{
            borderRadius: '8px',
            width: '100px',
            flexShrink: 0,
          }}
          variant="contained"
          loading={loading}
          onClick={handleRefresh}
        >
          Refresh
        </StyledLoadingButton>
      </Box>

      {seasonEndDate && (
        <Alert color="orange" sx={{ mb: 2 }}>
          Once the season ends on {formatGraphDateAndTime(seasonEndDate)}, scores from this season can no longer be removed.
        </Alert>
      )}

      {error && (
        <Grid item={true}>
          <Typography color="error">{error}</Typography>
        </Grid>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Table sx={{ tableLayout: 'auto', '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }} stickyHeader>
          <TableHead sx={{ '& .MuiTableCell-root': { backgroundColor: COLORS.black[70], textAlign: 'center' } }}>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>User created at</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Score{` (${leaderboardOptions.scoreLabel})`}</TableCell>
              <TableCell>Created at</TableCell>
              <TableCell>Updated at</TableCell>
              <TableCell
                sx={{
                  position: 'sticky',
                  right: 0,
                  backgroundColor: COLORS.black[70],
                  zIndex: 3,
                  boxShadow: '-2px 0 4px -2px rgba(0,0,0,0.3)',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No scores found for the current season.</TableCell>
              </TableRow>
            ) : (
              scores.map((score) => {
                const isDeleted = deletedScores.has(score.userId);
                const isFlagged = score.isFlagged && !isDeleted && !approvedFlaggedScores.has(score.userId);
                return (
                  <TableRow
                    key={score.userId}
                    style={{
                      backgroundColor: getBackgroundColor(isFlagged, isDeleted),
                    }}
                  >
                    <TableCell align="right">
                      {isFlagged ? (
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <Box>{score.flaggedReasons?.map((reason, idx) => <div key={idx}>{reason}</div>) ?? 'Unknown reasons'}</Box>
                          }
                        >
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Flag style={{ color: COLORS.warning[100] }} />
                            {score.userRank}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          {score.userRank}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{score.userId}</TableCell>
                    <TableCell>
                      <a href={getUserProfileUrl(score.username)} target="_blank" rel="noopener noreferrer">
                        {score.username}
                      </a>
                    </TableCell>
                    <TableCell align="center">{formatGraphDateAndTime(score.userCreatedAt)}</TableCell>
                    <TableCell align="center">{score.userCountryCode}</TableCell>
                    <TableCell align="right">{formatScore(score.userScore)}</TableCell>
                    <TableCell align="center">{formatGraphDateAndTime(score.createdAt)}</TableCell>
                    <TableCell align="center">{formatGraphDateAndTime(score.updatedAt)}</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        right: 0,
                        backgroundColor: getBackgroundColor(isFlagged, isDeleted) ?? COLORS.black[70],
                      }}
                    >
                      {isFlagged && (
                        <IconButton onClick={() => handleApproveFlaggedScoreClick(score)}>
                          <CheckIcon />
                        </IconButton>
                      )}
                      {!isDeleted && (
                        <IconButton onClick={() => handleDeleteClick(score)} disabled={isDeleted}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'white',
              },
            }}
          />
        </Box>
      )}

      <ProcessScoreDialog
        type="delete"
        open={deleteDialogOpen}
        scoreToProcess={scoreToDelete}
        scoreLabel={leaderboardOptions.scoreLabel}
        processing={deleting}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <ProcessScoreDialog
        type="approve"
        open={approveFlaggedScoreDialogOpen}
        scoreToProcess={flaggedScoreToApprove}
        scoreLabel={leaderboardOptions.scoreLabel}
        processing={approvingFlaggedScore}
        onCancel={handleApproveFlaggedScoreCancel}
        onConfirm={handleApproveFlaggedScoreConfirm}
      />
    </div>
  );
};

function getBackgroundColor(isFlagged: boolean, isDeleted: boolean): string | undefined {
  if (isFlagged) {
    return COLORS.warning[200];
  }
  if (isDeleted) {
    return COLORS.alert[200];
  }
  return undefined;
}

export default SubmissionTabLeaderboard;
